import React from 'react';
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import PdfHeader from './components/reusable/PdfHeader';
import PageNumber from './components/reusable/PageNumber';
import ServiceTechnicianSection from './components/reusable/ServiceTechnicianSection';
import ServicePartsSection from './components/reusable/ServicePartsSection';

const styles = StyleSheet.create({
    body: {
        padding: 15,
        paddingBottom: 25,
    },
    section: {
        marginTop: 8,
        padding: 6,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 3,
        backgroundColor: '#f8f9fa',
    },
    sectionTitle: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 6,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dee2e6',
        color: '#495057',
        textAlign: 'center',
    },
    twoColRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 6,
        width: '100%',
    },
    colHalf: {
        width: '49%'
    },
    labelRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        marginBottom: 3,
        flexWrap: 'wrap',
    },
    label: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: '#495057',
        marginRight: 4,
    },
    value: {
        fontSize: 9,
        fontFamily: 'Helvetica',
        color: '#212529',
        flexShrink: 1,
    },
    textContent: {
        fontSize: 9,
        fontFamily: 'Helvetica',
        lineHeight: 1.3,
    },
});

interface SalesOrderPdfProps {
    data: {
        baseSheet: any;
        sales: any; // formData from SalesOrderEdit
        parts: any[];
        techs: any[];
    };
    user: any;
    showFullDetails?: boolean; // controls pricing visibility, internal details
}

const SalesOrderPdf: React.FC<SalesOrderPdfProps> = ({ data, user, showFullDetails = true }) => {
    const { baseSheet, sales, parts = [], techs = [] } = data;

    const sheetForHeader = {
        ...baseSheet,
        id: baseSheet?.id || baseSheet?.sheetId,
        title: 'Sales Order',
    };

    const shipToLine = [sales?.city, sales?.state, sales?.zipCode]
        .filter(Boolean)
        .join(', ');

    return (
        <Document>
            <Page size="A4" style={styles.body}>
                <PdfHeader company={baseSheet.company} sheet={sheetForHeader} />

                {/* Order Details */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Order Details</Text>
                    <View style={styles.twoColRow}>
                        <View style={styles.colHalf}>
                            <View style={styles.labelRow}>
                                <Text style={styles.label}>Sales Rep:</Text>
                                <Text style={styles.value}>{sales?.salesRep || '—'}</Text>
                            </View>
                            <View style={styles.labelRow}>
                                <Text style={styles.label}>Terms:</Text>
                                <Text style={styles.value}>{sales?.terms || '—'}</Text>
                            </View>
                            <View style={styles.labelRow}>
                                <Text style={styles.label}>Address:</Text>
                                <Text style={styles.value}>{sales?.address || '—'}</Text>
                            </View>
                        </View>
                        <View style={styles.colHalf}>
                            <View style={styles.labelRow}>
                                <Text style={styles.label}>Shipped Via:</Text>
                                <Text style={styles.value}>{sales?.shippedVia || '—'}</Text>
                            </View>
                            <View style={styles.labelRow}>
                                <Text style={styles.label}>Ship To Name:</Text>
                                <Text style={styles.value}>{sales?.name || '—'}</Text>
                            </View>
                            {shipToLine ? (
                                <View style={styles.labelRow}>
                                    <Text style={styles.label}>City/State/Zip:</Text>
                                    <Text style={styles.value}>{shipToLine}</Text>
                                </View>
                            ) : null}
                        </View>
                    </View>
                </View>

                {/* Comments / Notes */}
                {sales?.notes ? (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Comments</Text>
                        <Text style={styles.textContent}>{sales.notes}</Text>
                    </View>
                ) : null}

                {/* Parts */}
                {parts && parts.length > 0 ? (
                    <ServicePartsSection parts={parts} />
                ) : null}

                {/* Technicians */}
                {techs && techs.length > 0 ? (
                    <ServiceTechnicianSection techs={techs} />
                ) : null}

                <PageNumber stage={data.sales.stage} sheetType="Sales Order" />
            </Page>
        </Document>
    );
};

export default SalesOrderPdf;
